import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Divider} from 'shells/layout/divider';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import type {UserFacingEntity} from '../../../core-entity/rows/peep-rows/types';

import {accountRefetchForEvent} from '../../account/hooks/use-account-update-refetch';
import {contactRefetchForEvent} from '../../contact/hooks/use-contact-update-refetch';
import type {PeepType} from '../../types';

import {AddressRowList} from './components/address-row-list';
import {BusinessCardWidgetHeader} from './components/business-card-widget-header';
import {EmailRowList} from './components/email-row-list';
import {PhoneRowList} from './components/phone-row-list';
import {UrlRowList} from './components/url-row-list';
import {useAddPeepOmnifield} from './graphql/hooks/use-add-peep-omnifield';
import {useGetPeepBusinessCardInfo} from './graphql/hooks/use-get-peep-business-card-info';
import {useGetPeepLabelOptions} from './graphql/hooks/use-get-peep-label-options';
import {
    getNewlyAddedId,
    getEmptyStateProps,
    splitByFormerStatus,
    getTabProps,
    sortByPrimaryStatus,
} from './helpers';
import {useBusinessCardFieldHandlers} from './hooks/use-business-card-field-handlers';
import type {RowErrorMessage} from './types';

interface Props {
    widget: WidgetFragment;
    entityId: string;
    peepType: PeepType;
}

const SUCCESS_FLASH_DURATION_MS = 2000;

export function BusinessCardWidget(props: Props) {
    const textFieldRef = useRef<HTMLInputElement | null>(null);
    const clearNewlyAddedRowIdTimeoutRef = useRef<number | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null | undefined>(null);
    const [rowErrorMessage, setRowErrorMessage] = useState<RowErrorMessage | null>(null);
    const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState<'current' | 'former'>('current');

    useEffect(() => {
        return () => {
            if (clearNewlyAddedRowIdTimeoutRef.current) {
                clearTimeout(clearNewlyAddedRowIdTimeoutRef.current);
                clearNewlyAddedRowIdTimeoutRef.current = null;
            }
        };
    }, []);

    const {peepBusinessCardInfo, isLoading: isLoadingBusinessCardInfo} = useGetPeepBusinessCardInfo(
        props.entityId
    );
    const {user} = useGetSessionUser();
    const {peepLabelOptions, isLoading: isLoadingLabelOptions} = useGetPeepLabelOptions();

    const {addPeepOmnifield} = useAddPeepOmnifield();

    const parentEntity: UserFacingEntity = props.peepType === 'account' ? 'company' : 'person';

    const {patchAddress, patchEmail, patchPhone, patchUrl} = useBusinessCardFieldHandlers(
        props.entityId,
        props.peepType,
        setRowErrorMessage
    );

    const emptyStateProps = useMemo(() => {
        return getEmptyStateProps(peepBusinessCardInfo);
    }, [peepBusinessCardInfo]);

    const focusTextField = useCallback(() => {
        setSelectedTab('current');
        requestAnimationFrame(() => {
            textFieldRef.current?.focus();
        });
    }, []);

    const handleAdd = useCallback(
        (textFieldValue: string) => {
            const beforeInfo = peepBusinessCardInfo;

            return addPeepOmnifield({entityId: props.entityId, value: textFieldValue}).then(
                (result) => {
                    const afterInfo = result?.data?.entityOmnifieldAdd?.entity;

                    if (!afterInfo) {
                        return result;
                    }

                    const didAddPostalAddress =
                        afterInfo.postalAddresses.all.length >
                        (beforeInfo?.postalAddresses.all.length ?? 0);

                    const newlyAddedId = getNewlyAddedId(beforeInfo, afterInfo);
                    if (newlyAddedId) {
                        setNewlyAddedRowId(newlyAddedId);
                        if (clearNewlyAddedRowIdTimeoutRef.current) {
                            clearTimeout(clearNewlyAddedRowIdTimeoutRef.current);
                        }
                        clearNewlyAddedRowIdTimeoutRef.current = window.setTimeout(() => {
                            setNewlyAddedRowId(null);
                            clearNewlyAddedRowIdTimeoutRef.current = null;
                        }, SUCCESS_FLASH_DURATION_MS);
                    }

                    if (props.peepType === 'account') {
                        accountRefetchForEvent('businessCardUpdated');
                        if (didAddPostalAddress) {
                            accountRefetchForEvent('locationUpdated');
                        }
                    } else {
                        contactRefetchForEvent('businessCardUpdated');
                        if (didAddPostalAddress) {
                            contactRefetchForEvent('locationUpdated');
                        }
                    }

                    return result;
                }
            );
        },
        [addPeepOmnifield, peepBusinessCardInfo, props.entityId, props.peepType]
    );

    const {current: currentAddresses, former: formerAddresses} = splitByFormerStatus(
        peepBusinessCardInfo?.postalAddresses.all
    );
    const currentAddressesSorted = sortByPrimaryStatus(currentAddresses);

    const {current: currentEmails, former: formerEmails} = splitByFormerStatus(
        peepBusinessCardInfo?.emailAddresses.all
    );
    const currentEmailsSorted = sortByPrimaryStatus(currentEmails);

    const {current: currentPhones, former: formerPhones} = splitByFormerStatus(
        peepBusinessCardInfo?.phones.all
    );
    const currentPhonesSorted = sortByPrimaryStatus(currentPhones);

    const urlsSorted = sortByPrimaryStatus(peepBusinessCardInfo?.urls.all);

    const hasFormerItems =
        formerAddresses.length > 0 || formerEmails.length > 0 || formerPhones.length > 0;

    const tabs = useMemo(() => {
        return getTabProps(hasFormerItems, selectedTab, setSelectedTab);
    }, [hasFormerItems, selectedTab, setSelectedTab]);

    const addressesForTab = selectedTab === 'current' ? currentAddressesSorted : formerAddresses;
    const hasAddressesForTab = addressesForTab.length > 0;

    const emailsForTab = selectedTab === 'current' ? currentEmailsSorted : formerEmails;
    const hasEmailsForTab = emailsForTab.length > 0;

    const phonesForTab = selectedTab === 'current' ? currentPhonesSorted : formerPhones;
    const hasPhonesForTab = phonesForTab.length > 0;

    const urlsForTab = selectedTab === 'current' ? urlsSorted : null;
    const hasUrlsForTab = Boolean(urlsForTab?.length);

    return (
        <CollapsibleCardWidget
            widget={props.widget}
            emptyStateProps={emptyStateProps}
            isLoading={isLoadingBusinessCardInfo || isLoadingLabelOptions}
            addButtonProps={{
                onClick: focusTextField,
                variant: 'singular',
                label: '+ Add info',
            }}
            tabProps={{
                tabs: tabs,
                headerContent: (
                    <BusinessCardWidgetHeader
                        textFieldRef={textFieldRef}
                        onAdd={handleAdd}
                        onFocus={() => setSelectedTab('current')}
                        setErrorMessage={setErrorMessage}
                    />
                ),
            }}
            errorProps={{
                error: errorMessage,
                setError: setErrorMessage,
            }}
        >
            <AddressRowList
                addresses={addressesForTab}
                canMarkAsPrimary={selectedTab === 'current' && hasAddressesForTab}
                parentEntity={parentEntity}
                labelOptions={peepLabelOptions?.postalAddresses}
                newlyAddedId={newlyAddedRowId}
                onUpdateAddress={patchAddress}
                onUpdateTitle={patchAddress}
                rowErrorMessage={rowErrorMessage}
                setRowErrorMessage={setRowErrorMessage}
            />
            {hasEmailsForTab && hasAddressesForTab && (
                <Divider variant='line--horizontal' spacing={8} />
            )}
            <EmailRowList
                entityId={props.entityId}
                emailAddresses={emailsForTab}
                canMarkAsPrimary={selectedTab === 'current' && hasEmailsForTab}
                parentEntity={parentEntity}
                labelOptions={peepLabelOptions?.emailAddresses}
                newlyAddedId={newlyAddedRowId}
                onUpdateEmail={patchEmail}
                onUpdateTitle={patchEmail}
                rowErrorMessage={rowErrorMessage}
                setRowErrorMessage={setRowErrorMessage}
                sendFromNutshellEnabled={user?.sendFromNutshellEnabled}
            />
            {hasPhonesForTab && (hasEmailsForTab || hasAddressesForTab) && (
                <Divider variant='line--horizontal' spacing={8} />
            )}
            <PhoneRowList
                entityId={props.entityId}
                phones={phonesForTab}
                canMarkAsPrimary={selectedTab === 'current' && hasPhonesForTab}
                parentEntity={parentEntity}
                labelOptions={peepLabelOptions?.phones}
                user={user}
                newlyAddedId={newlyAddedRowId}
                onUpdatePhone={patchPhone}
                onUpdateTitle={patchPhone}
                rowErrorMessage={rowErrorMessage}
                setRowErrorMessage={setRowErrorMessage}
            />
            {hasUrlsForTab && (hasPhonesForTab || hasEmailsForTab || hasAddressesForTab) && (
                <Divider variant='line--horizontal' spacing={8} />
            )}
            <UrlRowList
                urls={urlsForTab}
                canMarkAsPrimary={selectedTab === 'current' && hasUrlsForTab}
                parentEntity={parentEntity}
                labelOptions={peepLabelOptions?.urls}
                newlyAddedId={newlyAddedRowId}
                onUpdateUrl={patchUrl}
                onUpdateTitle={patchUrl}
                rowErrorMessage={rowErrorMessage}
                setRowErrorMessage={setRowErrorMessage}
            />
        </CollapsibleCardWidget>
    );
}
